import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {Subject, merge, finalize} from 'rxjs';
import {debounceTime, takeUntil} from 'rxjs/operators';
import {FieldFilter} from '@6grain/masofa-monolith-api-client/crop-monitoring/model/fieldFilter';
import {BidMapDetailsDto} from '@6grain/masofa-monolith-api-client/crop-monitoring/model/bidMapDetailsDto';
import {OpenStreetMapComponent} from '@components/openstreetmap/openstreetmap.component';
import {MapControllerInput, MapInitOptions} from '@components/openstreetmap/interfaces';
import {GisPolygon} from '@components/openstreetmap/interfaces/gis-component.interfaces';
import {RegionManagerService} from '@components/openstreetmap/managers/region-manager.service';
import {BidMapManagerService} from '@app/pages/crop-monitoring/pages/gis-bids/managers/bid-map-manager.service';
import {BidService} from '@6grain/masofa-monolith-api-client/crop-monitoring/api/bid.service';
import {bidMapZoomBand, type BidMapZoomBand} from '@app/pages/crop-monitoring/pages/gis-bids/request/bid-map-zoom.config';
import {getBidMapBounds} from '@app/pages/crop-monitoring/pages/gis-bids/request/bid-map-bounds.util';
import {GIS_BIDS_MAP_HEIGHT_CSS} from '@app/pages/crop-monitoring/pages/gis-bids/request/gis-bids-map-layout.constants';
import {environment} from '@root/src/environments/environment';

@Component({
  selector: 'app-gis-bids',
  standalone: true,
  imports: [CommonModule, TranslateModule, OpenStreetMapComponent],
  templateUrl: './gis-bids.html',
  styleUrl: './gis-bids.scss',
})
export class GisBidsComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  @Input() filtersParams: FieldFilter[] = [];
  totalCount = 0;

  @ViewChild(OpenStreetMapComponent) openstreetmapComponent!: OpenStreetMapComponent;

  private readonly bidMapManager = inject(BidMapManagerService);
  private readonly bidService = inject(BidService);
  private readonly regionManager = inject(RegionManagerService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translate = inject(TranslateService);
  private readonly ngZone = inject(NgZone);

  /** "Country / … / district" — same logic as gis-fields; plain text, not a back control. */
  regionPathDisplay = '';

  private readonly destroy$ = new Subject<void>();
  private readonly cancelRequest$ = new Subject<void>();
  private readonly mapViewportCancel$ = merge(this.cancelRequest$, this.destroy$);

  private mapViewportEventsBound = false;
  private regionsLoaded = false;

  /** Last zoom from map — drives which Bid map API is used. */
  mapZoom = 6;
  private previousZoomBand: BidMapZoomBand | null = null;

  mapController$ = new Subject<MapControllerInput>();

  mapOptions: MapInitOptions = {
    height: GIS_BIDS_MAP_HEIGHT_CSS,
    center: [environment.map.lat, environment.map.lng],
    zoom: 6,
    showControls: true,
    minimalControls: false,
    drawingMode: false,
    showDrawingControls: true,
    baseMap: 'osm',
    showRegionNames: false,
    disableBounds: false,
  };

  private bidMarkerIds: string[] = [];
  private bidPolygonIds: string[] = [];

  /**
   * Last details payload — used to rebuild marker/field popups on language change without refetch
   * (same idea as {@link SeasonManager} `seasons$` + `updateDisplay` on `onLangChange`).
   */
  private lastBidMapDetails: BidMapDetailsDto | null = null;

  ngOnInit(): void {
    this.mapOptions.center = [environment.map.lat, environment.map.lng];
    this.mapOptions.zoom = 6;
    this.mapOptions.height = GIS_BIDS_MAP_HEIGHT_CSS;
    this.regionPathDisplay = this.getRegionPathString();

    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.ngZone.run(() => {
        this.regionPathDisplay = this.getRegionPathString();
        this.refreshBidsMapAfterLangChange();
        this.cdr.markForCheck();
      });
    });

    void this.refreshTotalCount();
  }

  ngAfterViewInit(): void {
    this.cdr.markForCheck();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filtersParams'] && !changes['filtersParams'].firstChange) {
      void this.refreshTotalCount();
      this.scheduleReload(false);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onMapReady(): void {
    this.syncZoomFromMap();
    this.loadRegionsOnce();
    if (!this.mapViewportEventsBound) {
      this.bindMapViewportEvents();
    }
    setTimeout(() => {
      this.mapController$.next({invalidateSize: true});
    }, 200);
  }

  private loadRegionsOnce(): void {
    if (this.regionsLoaded) {
      return;
    }
    const osm = this.openstreetmapComponent;
    this.setOsmLoading(true);
    this.regionManager
      .toPolygon()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: polygons => {
          this.setOsmLoading(false);
          this.regionsLoaded = true;
          if (polygons?.length) {
            this.regionManager.configurePolygonEvents(polygons, this.mapOptions, undefined);
            this.attachRegionPathRefreshOnHover(polygons);
            osm.mapController$.next({addGisPolygons: polygons});

            setTimeout(() => {
              this.mapOptions.showRegionNames = true;
              this.toggleRegionNames();
              this.refreshRegionPathDisplay();
            });

            this.regionPathDisplay = this.getRegionPathString();
          }
          this.scheduleReload(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.setOsmLoading(false);
          this.scheduleReload(false);
          this.cdr.markForCheck();
        },
      });
  }

  private bindMapViewportEvents(): void {
    const osm = this.openstreetmapComponent;
    if (!osm?.mapZoomChange$ || !osm.mapDragEnd$) {
      return;
    }
    this.mapViewportEventsBound = true;

    osm.mapZoomChange$
      .pipe(takeUntil(this.destroy$))
      .subscribe(zoom => {
        this.mapZoom = zoom;
        const band = bidMapZoomBand(zoom);
        const prevBand = this.previousZoomBand;

        if (band === 'agg') {
          if (prevBand !== 'agg') {
            this.previousZoomBand = band;
            this.scheduleReload(false);
          } else {
            this.previousZoomBand = band;
          }
          return;
        }

        /* cluster / detail: bbox-driven — reload on every zoom; viewport bounds change even inside the same band. */
        this.previousZoomBand = band;
        this.scheduleReload(false);
      });

    osm.mapDragEnd$
      .pipe(takeUntil(this.destroy$), debounceTime(400))
      .subscribe((evt: {zoom?: number}) => {
        const z = evt?.zoom ?? this.mapZoom;
        const band = bidMapZoomBand(z);
        if (band === 'cluster' || band === 'detail') {
          this.scheduleReload(false);
        }
      });
  }

  /** Coalesce reload when zoom fires twice in one frame. */
  private scheduleReload(_debounced: boolean): void {
    queueMicrotask(() => {
      void this.refreshMapData();
    });
  }

  private syncZoomFromMap(): void {
    try {
      const z = this.openstreetmapComponent?.getMapInstance?.()?.getZoom?.();
      if (typeof z === 'number' && !Number.isNaN(z)) {
        this.mapZoom = z;
        this.previousZoomBand = bidMapZoomBand(z);
      }
    } catch {
      /* ignore */
    }
  }

  private async refreshMapData(): Promise<void> {
    const osm = this.openstreetmapComponent;
    if (!osm?.isMapInitialized) {
      return;
    }

    this.lastBidMapDetails = null;
    this.cancelRequest$.next();
    this.clearBidLayers(osm);

    const band = bidMapZoomBand(this.mapZoom);
    const filters = await this.regionManager.includeChildInFilters([...(this.filtersParams ?? [])] as any) as FieldFilter[];

    if (band === 'agg') {
      this.loadAggregates(osm, filters);
      return;
    }

    const bounds = getBidMapBounds(osm);
    if (!bounds) {
      return;
    }

    if (band === 'cluster') {
      this.loadClusters(osm, bounds, filters);
      return;
    }

    this.loadDetails(osm, bounds, filters);
  }

  private clearBidLayers(osm: OpenStreetMapComponent): void {
    if (this.bidMarkerIds.length) {
      osm.mapController$.next({removeMarkers: [...this.bidMarkerIds]});
    }
    if (this.bidPolygonIds.length) {
      osm.mapController$.next({removePolygons: [...this.bidPolygonIds]});
    }
    osm.mapController$.next({clearCircleMarkers: true});
    this.bidMarkerIds = [];
    this.bidPolygonIds = [];
  }

  private loadAggregates(osm: OpenStreetMapComponent, filters: FieldFilter[]): void {
    this.setOsmLoading(true);
    this.bidMapManager
      .loadRegionAggregates(filters, osm)
      .pipe(
        takeUntil(this.mapViewportCancel$),
        finalize(() => {
          this.setOsmLoading(false);
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: circles => {
          if (circles.length) {
            osm.mapController$.next({addCircleMarkers: circles});
          }
        },
      });
  }

  private loadClusters(
    osm: OpenStreetMapComponent,
    bounds: {minLat: number; maxLat: number; minLng: number; maxLng: number},
    filters: FieldFilter[],
  ): void {
    this.setOsmLoading(true);
    this.bidMapManager
      .loadClusters(bounds, filters, osm)
      .pipe(
        takeUntil(this.mapViewportCancel$),
        finalize(() => {
          this.setOsmLoading(false);
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: ({circles, pointMarkers}) => {
          const markers = [...pointMarkers];
          if (circles.length) {
            osm.mapController$.next({addCircleMarkers: circles});
          }
          if (markers.length) {
            this.bidMarkerIds = markers.map(m => m.id);
            osm.mapController$.next({mergeMarkers: markers});
          }
        },
      });
  }

  private loadDetails(
    osm: OpenStreetMapComponent,
    bounds: {minLat: number; maxLat: number; minLng: number; maxLng: number},
    filters: FieldFilter[],
  ): void {
    this.setOsmLoading(true);
    this.bidMapManager
      .loadDetails(bounds, filters)
      .pipe(
        takeUntil(this.mapViewportCancel$),
        finalize(() => {
          this.setOsmLoading(false);
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: dto => {
          this.lastBidMapDetails = dto ?? null;
          const {polygons, markers} = this.bidMapManager.detailsToLayers(dto);
          if (polygons.length) {
            this.bidPolygonIds = polygons.map(p => String(p.id));
            osm.mapController$.next({addGisPolygons: polygons});
          }
          if (markers.length) {
            this.bidMarkerIds = markers.map(m => m.id);
            osm.mapController$.next({mergeMarkers: markers});
          }
        },
      });
  }

  private setOsmLoading(loading: boolean): void {
    if (this.openstreetmapComponent) {
      this.openstreetmapComponent.isLoading = loading;
    }
  }

  /**
   * Rebuilds bid/field layers from cached details when possible; otherwise full reload (aggregates / clusters need API).
   * Mirrors {@link SeasonManager.setupLangRefresh} → {@link SeasonManager.updateDisplay}.
   */
  private refreshBidsMapAfterLangChange(): void {
    const osm = this.openstreetmapComponent;
    if (!osm?.isMapInitialized) {
      return;
    }
    const band = bidMapZoomBand(this.mapZoom);
    if (band === 'detail' && this.lastBidMapDetails) {
      this.clearBidLayers(osm);
      const {polygons, markers} = this.bidMapManager.detailsToLayers(this.lastBidMapDetails);
      if (polygons.length) {
        this.bidPolygonIds = polygons.map(p => String(p.id));
        osm.mapController$.next({addGisPolygons: polygons});
      }
      if (markers.length) {
        this.bidMarkerIds = markers.map(m => m.id);
        osm.mapController$.next({mergeMarkers: markers});
      }
      return;
    }
    this.scheduleReload(false);
  }

  /**
   * Same pattern as {@link GisFieldsComponent.toggleRegionNames}.
   */
  private toggleRegionNames(): void {
    this.mapController$.next({
      setShowRegionNames: this.mapOptions.showRegionNames,
    });
  }

  /**
   * Same hierarchy string as gis-fields back-button title: country / region / district / ALL_REGIONS.
   */
  getRegionPathString(): string {
    return this.regionManager.getRegionPathString(
      this.translate.instant(environment.country),
      this.translate.instant('ALL_REGIONS'),
    );
  }

  /**
   * Leaflet hover runs outside Angular — refresh the path label like gis-fields would need for a bound title.
   */
  private attachRegionPathRefreshOnHover(polygons: GisPolygon[]): void {
    for (const polygon of polygons) {
      const prevHover = polygon.onHover;
      const prevHoverOut = polygon.onHoverOut;
      polygon.onHover = (poly: GisPolygon) => {
        prevHover?.(poly);
        this.refreshRegionPathDisplay();
      };
      polygon.onHoverOut = (poly: GisPolygon) => {
        prevHoverOut?.(poly);
        this.refreshRegionPathDisplay();
      };
    }
  }

  private refreshRegionPathDisplay(): void {
    this.ngZone.run(() => {
      this.regionPathDisplay = this.getRegionPathString();
      this.cdr.markForCheck();
    });
  }

  private async refreshTotalCount(): Promise<void> {
    const filters = await this.regionManager.includeChildInFilters([...(this.filtersParams ?? [])] as any);
    this.bidService
      .cropMonitoringBidGetTotalCountPost({filters: filters as FieldFilter[]})
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (count: number) => {
          this.totalCount = Number(count) || 0;
          this.cdr.markForCheck();
        },
        error: () => {
          this.totalCount = 0;
          this.cdr.markForCheck();
        },
      });
  }
}
